import React, { useMemo, useState } from 'react';
import './ConfirmationGenerator.css';
import { camps } from '../data/camps';
import { DEPOSIT_PERCENT, BALANCE_DUE_DAYS } from '../constants/booking';
import { PAYMENT_DEFAULTS } from '../constants/payment';
import { parseDateOnlyLocal } from '../utils/dateUtils';
import { generateConfirmationPDF, ConfirmationPdfInput } from '../utils/pdfGenerator';

function countNights(checkIn: Date, checkOut: Date): number {
  return Math.max(
    0,
    Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))
  );
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function buildReference(guestName: string, checkIn: Date | null): string {
  const surname = guestName.trim().split(/\s+/).pop() || '';
  if (!surname || !checkIn || isNaN(checkIn.getTime())) return surname.toUpperCase();
  const day = String(checkIn.getDate()).padStart(2, '0');
  const month = checkIn.toLocaleString('en-AU', { month: 'short' }).toUpperCase();
  return `${surname.toUpperCase()}-${day}${month}`;
}

const ConfirmationGenerator: React.FC = () => {
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [campId, setCampId] = useState(camps[0]?.id ?? '');
  const [nightlyRate, setNightlyRate] = useState<number>(camps[0]?.pricePerNight ?? 0);
  const [checkInStr, setCheckInStr] = useState('');
  const [checkOutStr, setCheckOutStr] = useState('');
  const [guests, setGuests] = useState<number>(2);
  const [depositPercent, setDepositPercent] = useState<number>(DEPOSIT_PERCENT);
  const [referenceOverride, setReferenceOverride] = useState('');

  const [bankAccountName, setBankAccountName] = useState(PAYMENT_DEFAULTS.accountName);
  const [bankBsb, setBankBsb] = useState(PAYMENT_DEFAULTS.bsb);
  const [bankAccountNumber, setBankAccountNumber] = useState(PAYMENT_DEFAULTS.accountNumber);
  const [payId, setPayId] = useState(PAYMENT_DEFAULTS.payId);

  const [error, setError] = useState('');

  const selectedCamp = useMemo(() => camps.find((c) => c.id === campId) || null, [campId]);

  const checkIn = useMemo(
    () => (checkInStr ? parseDateOnlyLocal(checkInStr) : null),
    [checkInStr]
  );
  const checkOut = useMemo(
    () => (checkOutStr ? parseDateOnlyLocal(checkOutStr) : null),
    [checkOutStr]
  );

  const nights = checkIn && checkOut ? countNights(checkIn, checkOut) : 0;
  const total = nights * (Number.isFinite(nightlyRate) ? nightlyRate : 0);
  const depositAmount = Math.round(total * (depositPercent / 100) * 100) / 100;
  const balanceAmount = Math.round((total - depositAmount) * 100) / 100;
  const balanceDueDate = checkIn ? addDays(checkIn, -BALANCE_DUE_DAYS) : null;
  const reference =
    referenceOverride.trim() || buildReference(guestName, checkIn);

  const handleCampChange = (id: string) => {
    setCampId(id);
    const camp = camps.find((c) => c.id === id);
    if (camp) setNightlyRate(camp.pricePerNight);
  };

  const buildInput = (): ConfirmationPdfInput | null => {
    if (!guestName.trim()) {
      setError('Please enter the guest name.');
      return null;
    }
    if (!selectedCamp) {
      setError('Please select a camp.');
      return null;
    }
    if (!checkIn || !checkOut || isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
      setError('Please enter valid check-in and check-out dates.');
      return null;
    }
    if (nights <= 0) {
      setError('Check-out must be after check-in.');
      return null;
    }
    setError('');
    return {
      bookingRef: reference,
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim(),
      guestPhone: guestPhone.trim() || undefined,
      campName: selectedCamp.name,
      location: selectedCamp.location,
      checkIn,
      checkOut,
      nights,
      guests,
      total,
      depositPercent,
      depositAmount,
      balanceAmount,
      balanceDueDate: balanceDueDate as Date,
      bankAccountName: bankAccountName.trim(),
      bankBsb: bankBsb.trim(),
      bankAccountNumber: bankAccountNumber.trim(),
      payId: payId.trim() || undefined,
      paymentReference: reference,
    };
  };

  const handleDownload = () => {
    const input = buildInput();
    if (!input) return;
    const doc = generateConfirmationPDF(input);
    doc.save(`confirmation-${input.bookingRef || 'booking'}.pdf`);
  };

  const handlePreview = () => {
    const input = buildInput();
    if (!input) return;
    const doc = generateConfirmationPDF(input);
    window.open(doc.output('bloburl'), '_blank');
  };

  const money = (n: number) => `$${n.toFixed(2)}`;

  return (
    <div className="confgen-page">
      <div className="confgen-container">
        <header className="confgen-header">
          <h1>Booking Confirmation Generator</h1>
          <p>
            Fill in the details and download a professional PDF to send to the guest when you
            confirm their booking. Nothing is saved — the PDF is created in your browser.
          </p>
        </header>

        <div className="confgen-grid">
          <div className="confgen-form">
            <section className="confgen-section">
              <h2>Guest</h2>
              <label>
                Full name
                <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Sarah Mitchell" />
              </label>
              <div className="confgen-row">
                <label>
                  Email
                  <input value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="guest@example.com" />
                </label>
                <label>
                  Phone
                  <input value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="+61 4XX XXX XXX" />
                </label>
              </div>
            </section>

            <section className="confgen-section">
              <h2>Stay</h2>
              <div className="confgen-row">
                <label>
                  Camp
                  <select value={campId} onChange={(e) => handleCampChange(e.target.value)}>
                    {camps.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Nightly rate ($)
                  <input
                    type="number"
                    value={nightlyRate}
                    onChange={(e) => setNightlyRate(parseFloat(e.target.value) || 0)}
                  />
                </label>
              </div>
              <div className="confgen-row">
                <label>
                  Check-in
                  <input type="date" value={checkInStr} onChange={(e) => setCheckInStr(e.target.value)} />
                </label>
                <label>
                  Check-out
                  <input type="date" value={checkOutStr} onChange={(e) => setCheckOutStr(e.target.value)} />
                </label>
              </div>
              <div className="confgen-row">
                <label>
                  Guests
                  <input
                    type="number"
                    min={1}
                    value={guests}
                    onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                  />
                </label>
                <label>
                  Deposit (%)
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={depositPercent}
                    onChange={(e) => setDepositPercent(parseFloat(e.target.value) || 0)}
                  />
                </label>
              </div>
            </section>

            <section className="confgen-section">
              <h2>Payment details</h2>
              <label>
                Account name
                <input value={bankAccountName} onChange={(e) => setBankAccountName(e.target.value)} />
              </label>
              <div className="confgen-row">
                <label>
                  BSB
                  <input value={bankBsb} onChange={(e) => setBankBsb(e.target.value)} placeholder="000-000" />
                </label>
                <label>
                  Account number
                  <input value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="00000000" />
                </label>
              </div>
              <div className="confgen-row">
                <label>
                  PayID (optional)
                  <input value={payId} onChange={(e) => setPayId(e.target.value)} placeholder="email or mobile" />
                </label>
                <label>
                  Reference
                  <input
                    value={referenceOverride}
                    onChange={(e) => setReferenceOverride(e.target.value)}
                    placeholder={buildReference(guestName, checkIn) || 'SURNAME-DDMON'}
                  />
                </label>
              </div>
            </section>
          </div>

          <aside className="confgen-summary">
            <h2>Summary</h2>
            <div className="confgen-summary-row"><span>Nights</span><span>{nights}</span></div>
            <div className="confgen-summary-row"><span>Total</span><span>{money(total)}</span></div>
            <div className="confgen-summary-row highlight">
              <span>Deposit ({depositPercent}%)</span><span>{money(depositAmount)}</span>
            </div>
            <div className="confgen-summary-row"><span>Balance</span><span>{money(balanceAmount)}</span></div>
            <div className="confgen-summary-row">
              <span>Balance due</span>
              <span>{balanceDueDate ? balanceDueDate.toLocaleDateString() : '—'}</span>
            </div>
            <div className="confgen-summary-row"><span>Reference</span><span>{reference || '—'}</span></div>

            {error && <p className="confgen-error">{error}</p>}

            <button type="button" className="confgen-btn primary" onClick={handleDownload}>
              Download PDF
            </button>
            <button type="button" className="confgen-btn" onClick={handlePreview}>
              Preview
            </button>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationGenerator;
